import { getGroup, type GroupId } from '@lafayette/content';
import type { CSSProperties, ReactNode } from 'react';

import styles from './Card.module.css';

/** Optional decorative texture, keyed to the group's `pattern` in content. */
const PATTERNS: Record<string, string> = {
  solid: '',
  diagonal: styles.patternDiagonal!,
  dots: styles.patternDots!,
  grid: styles.patternGrid!,
  chevron: styles.patternChevron!,
  wave: styles.patternWave!,
  stripe: styles.patternStripe!,
  crosshatch: styles.patternCrosshatch!,
  check: styles.patternCheck!,
  bar: styles.patternBar!,
};

export function patternClass(pattern: string): string {
  return PATTERNS[pattern] ?? '';
}

export interface GroupChipProps {
  /** One group, or two for a wildcard's pair of colors. */
  groups: GroupId | GroupId[];
  size?: 'normal' | 'large';
  className?: string;
}

/**
 * A property group shown as pure color. The group's name goes in the
 * accessible name rather than on screen, so nothing depends on hue alone but
 * the interface stays free of color words.
 */
export function GroupChip({ groups, size = 'normal', className = '' }: GroupChipProps): ReactNode {
  const ids = Array.isArray(groups) ? groups : [groups];
  const rainbow = ids.length > 2;
  const primary = getGroup(ids[0]!);
  const secondary = ids[1] ? getGroup(ids[1]) : null;

  const style: CSSProperties = {
    ['--chip-color' as string]: primary.color,
    ...(secondary ? { ['--chip-color-2' as string]: secondary.color } : {}),
  };

  const classes = [
    styles.chip,
    size === 'large' ? styles.chipLarge : '',
    rainbow ? styles.chipRainbow : secondary ? styles.chipSplit : patternClass(primary.pattern),
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return <span className={classes} style={style} role="img" aria-label={describeGroups(ids)} />;
}

/** Words for a set of groups, used only for screen readers. */
export function describeGroups(groups: GroupId[]): string {
  if (groups.length > 2) return 'any color';
  return groups.map((id) => getGroup(id).label).join(' or ');
}
